import {
  formatDatetime,
  getDeviceInfo,
  isDefined,
  isNumber,
  isObject,
  isString,
  toDecimalPoints
} from "./utils.ts";
import { BACKGROUND, FOREGROUND, SDK_VERSION, VISIBLE } from "./constants.ts";
import Controller from "./Controller.ts";
import Logger from "./Logger.ts";
import type {
  ChunkDetails,
  DeviceInfo,
  Chunk,
  SystemEvent,
  AppEvent,
  AppInfo,
  NavigationEvent,
  SDKInfo
} from "./types/types.ts";

const ALL = "ALL";

export const makeSystemEvent = (name: string, time: unknown): SystemEvent => ({
  name: name === VISIBLE ? FOREGROUND : BACKGROUND,
  time: isNumber(time) ? toDecimalPoints(time as number) : 0
});

export const makeEvent = (
  name: string,
  additional_info: Record<string, unknown> = {},
  category: string | undefined,
  time: unknown
): AppEvent | undefined => {
  if (isString(name) && isObject(additional_info)) {
    return {
      name,
      additional_info,
      category: isDefined(category)
        ? isString(category)
          ? category
          : undefined
        : undefined,
      time: isNumber(time) ? toDecimalPoints(time as number) : 0
    };
  }
  return undefined;
};

export const makeAppInfo = (
  siteName: string,
  domain: string,
  installationTime: number,
  appMode: string,
  appVersion: string | null | undefined
): AppInfo => {
  const version = isDefined(appVersion) ? appVersion! : ALL;
  return {
    app_name: siteName,
    app_version: version,
    bundle_id: domain,
    installation_time: installationTime,
    app_mode: appMode
  };
};

export const getGeoLocation = (): Promise<
  { latitude: number; longitude: number } | undefined
> =>
  new Promise((resolve) => {
    if (!("geolocation" in navigator)) {
      Logger.error("Geolocation is not supported by this browser.");
      resolve(undefined);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      (error) => {
        Logger.error(`Error getting location: ${error.message}`);
        resolve(undefined);
      }
    );
  });

export const makeDeviceInfo = (
  id: string,
  hardware_model: string,
  dimensions: { width: number; height: number },
  os_version: string,
  dpi: string,
  dimensions_in_pixels: { width: number; height: number },
  browser: string | "web",
  browser_version: number | null
): DeviceInfo => ({
  id,
  hardware_model,
  dimensions,
  os_version,
  platform: "web",
  dpi,
  dimensions_in_pixels,
  $browser: browser,
  $browser_version: browser_version
});

export const makeNavigationEvent = (
  url: string,
  pageTitle: string,
  duration: unknown,
  time: unknown = 0
): NavigationEvent => ({
  navigation_id: url,
  name: pageTitle,
  duration: isNumber(duration) ? toDecimalPoints(duration as number) : 0,
  transition_time: isNumber(time) ? toDecimalPoints(time as number) : 0
});

export const DEFAULT_CHUNK_DETAILS: ChunkDetails = {
  navigation_events: [],
  app_events: [],
  system_events: [],
  user_events: [],
  chunk_attributes: {},
  user_attributes: {},
  session_attributes: {}
};

export const getSdkInfo = (launch_time: number): SDKInfo => ({
  sdk_version: SDK_VERSION,
  config_id: "klaritics",
  tick_interval: 1,
  chunk_retry_window: 1800,
  sdk_start_time: launch_time
});

export const chunkInitialState = (
  sdkStartTime: number,
  sessionStartTime: number,
  sessionId: string,
  chunkNumber = 1
): Chunk => {
  Logger.debug("Model : Building the initial state of the Chunk");
  return {
    sdk_info: getSdkInfo(sdkStartTime),
    session_info: {
      id: sessionId,
      launch_time: sessionStartTime,
      launch_time_string: formatDatetime(new Date(sessionStartTime)),
      startup_time: 0,
      is_background: false,
      is_first_session: false,
      duration: 0,
      total_duration: 0,
      process_name: location.hostname,
      is_fatal: false,
      launch_type: "normal"
    },
    app_info: {
      app_name: "",
      app_version: ALL,
      bundle_id: "",
      installation_time: Controller.getInstance().getInstallationTime(),
      app_mode: "production"
    },
    device_info: getDeviceInfo(window) as unknown as DeviceInfo,
    chunk_meta: {
      id: sessionId + ":" + chunkNumber,
      number: chunkNumber,
      reporting_times: []
    },
    chunk_details: {
      ...DEFAULT_CHUNK_DETAILS,
      user_attributes: {},
      session_attributes: {}
    }
  };
};
